import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Heart, CreditCard, RefreshCw, XCircle, 
  Clock, MessageCircle, CheckCircle, AlertTriangle, Mail
} from 'lucide-react';

export default function RefundPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="h-6 w-6 text-primary" />
              <span className="font-serif text-xl text-primary-900">PlayFirst</span>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
              <CreditCard className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-4">
              Refund & Cancellation Policy
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              We want you to be completely satisfied with PlayFirst. Here's everything you need to know 
              about our refund and cancellation policies.
            </p>
          </div>

          {/* Quick Summary */}
          <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-blue-200 mb-8" data-testid="policy-summary">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">At a Glance</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl p-4 text-center">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-stone-800">Case-by-Case Review</p>
                <p className="text-xs text-stone-500 mt-1">Every request is personally reviewed</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-green-600" />
                <p className="font-medium text-stone-800">Prompt Response</p>
                <p className="text-xs text-stone-500 mt-1">We respond within 48 hours</p>
              </div>
              <div className="bg-white rounded-2xl p-4 text-center">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                <p className="font-medium text-stone-800">Fair Resolution</p>
                <p className="text-xs text-stone-500 mt-1">We work to resolve issues before escalation</p>
              </div>
            </div>
          </Card>

          {/* Refund Policy */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="refund-policy">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900">Refund Policy</h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-2xl">
                <h3 className="font-medium text-primary-900 mb-2">Review Process</h3>
                <p className="text-stone-600">
                  Refund requests are reviewed on a <strong>case-by-case basis</strong>. We understand that 
                  circumstances vary, and we're committed to finding fair solutions for our users.
                </p>
              </div>

              <div className="p-4 bg-stone-50 rounded-2xl">
                <h3 className="font-medium text-primary-900 mb-2">When Refunds May Be Issued</h3>
                <ul className="space-y-2 text-stone-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>If you're dissatisfied shortly after billing (within 7 days)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>If technical issues prevent you from using the service</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>If features were misrepresented or unavailable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Duplicate or erroneous charges</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-amber-900 mb-1">Important Note</h3>
                    <p className="text-sm text-amber-800">
                      Refunds are issued at our discretion. We encourage you to take advantage of our 
                      7-day free trial to fully evaluate PlayFirst before subscribing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Cancellation Policy */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="cancellation-policy">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900">Cancellation Policy</h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-stone-50 rounded-2xl">
                <h3 className="font-medium text-primary-900 mb-2">How to Cancel</h3>
                <p className="text-stone-600 mb-3">
                  You can cancel your subscription at any time from your Settings page. No questions asked.
                </p>
                <ol className="list-decimal list-inside space-y-1 text-stone-600 text-sm">
                  <li>Go to Settings</li>
                  <li>Click on "Manage Subscription"</li>
                  <li>Select "Cancel Subscription"</li>
                  <li>Confirm your cancellation</li>
                </ol>
              </div>

              <div className="p-4 bg-stone-50 rounded-2xl">
                <h3 className="font-medium text-primary-900 mb-2">What Happens When You Cancel</h3>
                <ul className="space-y-2 text-stone-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Your subscription remains active until the end of your current billing period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>You won't be charged again after cancellation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>Your data is retained for 30 days in case you wish to resubscribe</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>After 30 days, you can request permanent data deletion</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200">
                <h3 className="font-medium text-blue-900 mb-2">Simple, no lock-in</h3>
                <p className="text-sm text-blue-800">
                  You can cancel any time. Once cancelled, your subscription won&apos;t renew,
                  and your account becomes inactive at the end of the current billing period.
                  We won&apos;t bill you again.
                </p>
              </div>
            </div>
          </Card>

          {/* Dispute Resolution */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="dispute-resolution">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900">Dispute Resolution</h2>
            </div>

            <p className="text-stone-600 mb-4">
              Our support team responds promptly to resolve any disputes before escalation. 
              We believe in open communication and fair treatment.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-purple-600">1</span>
                </div>
                <div>
                  <p className="font-medium text-stone-800">Contact Us First</p>
                  <p className="text-sm text-stone-600">Reach out to our support team with your concern</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-purple-600">2</span>
                </div>
                <div>
                  <p className="font-medium text-stone-800">48-Hour Response</p>
                  <p className="text-sm text-stone-600">We'll respond within 48 hours with a proposed resolution</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-purple-600">3</span>
                </div>
                <div>
                  <p className="font-medium text-stone-800">Fair Resolution</p>
                  <p className="text-sm text-stone-600">We'll work together to find a solution that works for everyone</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Plan Changes */}
          <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-8" data-testid="plan-changes">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900">Changing Your Plan</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-2xl">
                <h3 className="font-medium text-green-900 mb-2">Upgrading</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Takes effect immediately</li>
                  <li>• Prorated charge for remainder of billing cycle</li>
                  <li>• Instant access to new features</li>
                </ul>
              </div>
              <div className="p-4 bg-amber-50 rounded-2xl">
                <h3 className="font-medium text-amber-900 mb-2">Downgrading</h3>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• Takes effect at next billing cycle</li>
                  <li>• Keep current features until then</li>
                  <li>• Data preserved within new plan limits</li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Contact Information */}
          <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl border-0 mb-8" data-testid="contact-info">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-2xl font-serif text-primary-900">Need Help?</h2>
            </div>
            <p className="text-stone-600 mb-4">
              If you have questions about refunds, cancellations, or billing, we're here to help.
            </p>
            <div className="p-4 bg-white rounded-2xl">
              <p className="font-medium text-primary-900">PlayFirst Support</p>
              <p className="text-stone-600">Email: michaela@playfirstapp.com</p>
              <p className="text-stone-600">Response time: Within 48 hours</p>
            </div>
          </Card>

          {/* Last Updated */}
          <div className="text-center text-sm text-stone-500">
            <p>Last updated: February 2026</p>
            <p className="mt-2">
              This policy may be updated from time to time. We'll notify you of any significant changes.
            </p>
          </div>

        </motion.div>
      </main>
    </div>
  );
}
